/**
 * CITY DRESSING KIT — shared by the chibre_arena ("Block City") and
 * plumbing_plaza realism passes (mapExtras/chibre_arena.ts,
 * mapExtras/plumbing_plaza.ts).
 *
 * Every piece is plain DATA with an explicit world AABB, so
 * `agent/bench-extras-chibre.mjs` checks the collision contract against the
 * real shared boxes without three.js objects:
 *  - `wall`   : on a side face of `host`, ≤ WALL_PROUD out, below its top,
 *               inside its span, never inside another collider;
 *  - `ground` : a decal ≤ GROUND_LIFT on the floor slab, under no collider;
 *  - `top`    : flush (≤ TOP_LIFT) on a collider's top face, inside it.
 * `buildPieces` turns the list into ONE merged mesh per material.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import { mapTextures, surfaceMaps, worldProjectUV } from "./mapTextures.js";

export const WALL_PROUD = 0.26;
export const GROUND_LIFT = 0.05;
export const TOP_LIFT = 0.1;

export type Zone = "wall" | "ground" | "top";
export type Mat = "asphalt" | "pave" | "stone" | "paint" | "metal" | "steel" | "copper" | "shop" | "water" | "lamp";
/** quad faces: +x,-x,+z,-z,+y */
export type Facing = "px" | "nx" | "pz" | "nz" | "py";

export interface Piece {
  zone: Zone; mat: Mat; tag: string; host: number;
  shape: "box" | "cylX" | "cylY" | "cylZ" | "quad";
  x0: number; x1: number; y0: number; y1: number; z0: number; z1: number;
  facing?: Facing;
  color: number;
  /** atlas cell for `paint` / `shop` quads: [u0, v0, u1, v1] in 0..1 */
  uv?: [number, number, number, number];
  /** UV tile (m) for world-projected materials */
  tile?: number;
}

export interface AABB { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }
export const boxAABB = (b: MapBox): AABB => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
export const overlaps = (a: AABB, b: AABB, e = 0): boolean =>
  a.x0 < b.x1 - e && a.x1 > b.x0 + e && a.y0 < b.y1 - e && a.y1 > b.y0 + e && a.z0 < b.z1 - e && a.z1 > b.z0 + e;

/** deterministic hash in [0,1) */
export function h01(a: number, b: number, salt: number): number {
  let x = Math.imul((a * 73856093) ^ (b * 19349663) ^ (salt * 83492791), 0x9e3779b1);
  x ^= x >>> 15; x = Math.imul(x, 0x85ebca6b); x ^= x >>> 13;
  return ((x >>> 0) % 100000) / 100000;
}

/** One side face of a collider. t = the horizontal tangent axis coordinate. */
export interface Face {
  host: number; axis: "x" | "z"; sign: 1 | -1; plane: number;
  t0: number; t1: number; y0: number; y1: number;
}

/** Collision context: boxes, AABBs, floors, the playable rectangle. */
export class Scene {
  readonly A: AABB[];
  readonly floors = new Set<number>();
  /** playable XZ rectangle (inside the perimeter walls) */
  readonly play: { x0: number; x1: number; z0: number; z1: number };
  readonly out: Piece[] = [];
  constructor(readonly map: MapDef) {
    this.A = map.boxes.map(boxAABB);
    map.boxes.forEach((b, i) => {
      const a = this.A[i];
      if (Math.abs(a.y1) < 0.05 && b.s[0] > 20 && b.s[2] > 20) this.floors.add(i);
    });
    // perimeter walls = tall thin boxes; the play rect is inside them
    let x0 = -Infinity, x1 = Infinity, z0 = -Infinity, z1 = Infinity;
    map.boxes.forEach((b, i) => {
      if (b.s[1] < 10) return;
      const a = this.A[i];
      if (b.s[0] <= 1.6 && b.s[2] > 30) { if (b.p[0] < 0) x0 = Math.max(x0, a.x1); else x1 = Math.min(x1, a.x0); }
      if (b.s[2] <= 1.6 && b.s[0] > 30) { if (b.p[2] < 0) z0 = Math.max(z0, a.z1); else z1 = Math.min(z1, a.z0); }
    });
    this.play = { x0, x1, z0, z1 };
  }
  isWall(i: number): boolean {
    const b = this.map.boxes[i];
    return b.s[1] >= 10 && (b.s[0] <= 1.6 || b.s[2] <= 1.6);
  }
  /** free of every collider except `skip` and the floors */
  clear(r: AABB, skip = -1, e = 0.004): boolean {
    for (let i = 0; i < this.A.length; i++) {
      if (i === skip || this.floors.has(i)) continue;
      if (overlaps(r, this.A[i], e)) return false;
    }
    return true;
  }
  /** the four side faces of box i that face into the playable area */
  faces(i: number): Face[] {
    const a = this.A[i];
    const fs: Face[] = [
      { host: i, axis: "x", sign: 1, plane: a.x1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "x", sign: -1, plane: a.x0, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", sign: 1, plane: a.z1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", sign: -1, plane: a.z0, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
    ];
    const P = this.play;
    return fs.filter((f) => {
      const probe = f.plane + f.sign * 0.6, mid = (f.t0 + f.t1) / 2;
      const px = f.axis === "x" ? probe : mid, pz = f.axis === "z" ? probe : mid;
      return px > P.x0 && px < P.x1 && pz > P.z0 && pz < P.z1;
    }).map((f) => ({ ...f, y0: Math.max(f.y0, 0) }));
  }
  push(p: Piece): void { this.out.push(p); }

  /** A box (or cylinder / quad) on a face: tangent centre t, vertical centre
   *  y, width w, height h, `proud` out of the plane (embedded 1 cm). Returns
   *  false (nothing placed) when off the face or blocked. */
  onFace(f: Face, mat: Mat, tag: string, t: number, y: number, w: number, h: number, proud: number, color: number,
    o: { shape?: "box" | "cyl" | "quad"; uv?: [number, number, number, number]; tile?: number } = {}): boolean {
    if (t - w / 2 < f.t0 - 1e-6 || t + w / 2 > f.t1 + 1e-6 || y - h / 2 < f.y0 - 1e-6 || y + h / 2 > f.y1 + 1e-6) return false;
    proud = Math.min(proud, WALL_PROUD);
    const shape = o.shape ?? "box";
    const inner = shape === "quad" ? f.plane + f.sign * proud : f.plane - f.sign * 0.01;
    const outer = f.plane + f.sign * proud;
    const n0 = Math.min(inner, outer), n1 = Math.max(inner, outer);
    const r: AABB = f.axis === "x"
      ? { x0: n0, x1: n1, y0: y - h / 2, y1: y + h / 2, z0: t - w / 2, z1: t + w / 2 }
      : { x0: t - w / 2, x1: t + w / 2, y0: y - h / 2, y1: y + h / 2, z0: n0, z1: n1 };
    if (!this.clear(r, f.host)) return false;
    const facing: Facing = f.axis === "x" ? (f.sign > 0 ? "px" : "nx") : (f.sign > 0 ? "pz" : "nz");
    const cyl = f.axis === "x" ? "cylZ" : "cylX";
    this.push({
      zone: "wall", mat, tag, host: f.host, ...r, color,
      shape: shape === "quad" ? "quad" : shape === "cyl" ? (h > w ? "cylY" : cyl) : "box",
      facing: shape === "quad" ? facing : undefined, uv: o.uv, tile: o.tile,
    });
    return true;
  }
  /** a flat ground decal (top at `lift`), only where no collider stands */
  ground(mat: Mat, tag: string, x0: number, x1: number, z0: number, z1: number, lift: number, color: number,
    o: { quad?: boolean; uv?: [number, number, number, number]; tile?: number } = {}): boolean {
    lift = Math.min(lift, GROUND_LIFT);
    const r: AABB = { x0, x1, z0, z1, y0: o.quad ? lift : -0.02, y1: lift };
    if (!this.clear(r, -1, 0.002)) return false;
    this.push({ zone: "ground", mat, tag, host: -1, ...r, color, shape: o.quad ? "quad" : "box", facing: o.quad ? "py" : undefined, uv: o.uv, tile: o.tile });
    return true;
  }
  /** a flush piece on box i's top face */
  top(i: number, mat: Mat, tag: string, x0: number, x1: number, z0: number, z1: number, lift: number, color: number,
    o: { quad?: boolean; uv?: [number, number, number, number]; tile?: number } = {}): boolean {
    const a = this.A[i];
    if (x0 < a.x0 - 1e-6 || x1 > a.x1 + 1e-6 || z0 < a.z0 - 1e-6 || z1 > a.z1 + 1e-6) return false;
    lift = Math.min(lift, TOP_LIFT);
    const r: AABB = { x0, x1, z0, z1, y0: o.quad ? a.y1 + lift : a.y1 - 0.01, y1: a.y1 + lift };
    if (!this.clear(r, i)) return false;
    this.push({ zone: "top", mat, tag, host: i, ...r, color, shape: o.quad ? "quad" : "box", facing: o.quad ? "py" : undefined, uv: o.uv, tile: o.tile });
    return true;
  }
}

/**
 * Tile the floor between the colliders: an exact non-uniform grid over every
 * collider edge (and each collider edge pushed out by `ring`), each cell
 * classified as under-a-collider (skipped), within `ring` of one (`near`),
 * or open (`far`). Gives sidewalks / plaza aprons that hug each block exactly.
 */
export function floorCells(S: Scene, ring: number, isBlock: (i: number) => boolean,
  emit: (x0: number, x1: number, z0: number, z1: number, near: boolean) => void): void {
  const P = S.play;
  const xs = new Set<number>([P.x0, P.x1]), zs = new Set<number>([P.z0, P.z1]);
  const blocks: AABB[] = [];
  S.A.forEach((a, i) => {
    if (S.floors.has(i) || a.y0 > 0.5) return;
    if (a.x1 < P.x0 || a.x0 > P.x1 || a.z1 < P.z0 || a.z0 > P.z1) return;
    blocks.push(a);
    const near = isBlock(i);
    for (const v of near ? [a.x0 - ring, a.x0, a.x1, a.x1 + ring] : [a.x0, a.x1]) if (v > P.x0 && v < P.x1) xs.add(v);
    for (const v of near ? [a.z0 - ring, a.z0, a.z1, a.z1 + ring] : [a.z0, a.z1]) if (v > P.z0 && v < P.z1) zs.add(v);
  });
  const rings = S.A.filter((a, i) => !S.floors.has(i) && a.y0 <= 0.5 && isBlock(i));
  // split very long cells so world-projected UVs and merged buffers stay sane
  const split = (vs: number[]): number[] => {
    const out: number[] = [];
    for (let k = 0; k < vs.length - 1; k++) {
      const n = Math.max(1, Math.ceil((vs[k + 1] - vs[k]) / 12));
      for (let j = 0; j < n; j++) out.push(vs[k] + ((vs[k + 1] - vs[k]) * j) / n);
    }
    out.push(vs[vs.length - 1]);
    return out;
  };
  const X = split([...xs].sort((a, b) => a - b)), Z = split([...zs].sort((a, b) => a - b));
  for (let i = 0; i < X.length - 1; i++) for (let j = 0; j < Z.length - 1; j++) {
    const x0 = X[i], x1 = X[i + 1], z0 = Z[j], z1 = Z[j + 1];
    if (x1 - x0 < 1e-3 || z1 - z0 < 1e-3) continue;
    const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
    if (blocks.some((a) => cx > a.x0 && cx < a.x1 && cz > a.z0 && cz < a.z1)) continue;
    const near = rings.some((a) => cx > a.x0 - ring && cx < a.x1 + ring && cz > a.z0 - ring && cz < a.z1 + ring);
    emit(x0, x1, z0, z1, near);
  }
}

// ---------------------------------------------------------------------------
// paint atlas (road markings, manholes, grates, plaques) — 1024 × 512
export const ATLAS = {
  white: [0.0, 0.0, 0.03, 0.06], yellow: [0.04, 0.0, 0.07, 0.06],
  manhole: [0.0, 0.5, 0.25, 1.0], grate: [0.25, 0.75, 0.5, 1.0], drain: [0.25, 0.5, 0.5, 0.75],
  plaqueEaux: [0.5, 0.75, 1.0, 1.0], plaqueRue: [0.5, 0.5, 0.75, 0.75], gauge: [0.75, 0.5, 0.875, 0.75],
  hatch: [0.0, 0.125, 0.25, 0.375], wornWhite: [0.25, 0.0, 0.5, 0.125], oil: [0.5, 0.0, 0.75, 0.25], crack: [0.75, 0.0, 1.0, 0.25],
} as const satisfies Record<string, readonly [number, number, number, number]>;

let atlasTex: THREE.CanvasTexture | null = null;
function paintAtlas(): THREE.CanvasTexture {
  if (atlasTex) return atlasTex;
  const W = 1024, H = 512;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, W, H);
  // canvas y is DOWN, UV v is UP: region [u0,v0,u1,v1] → canvas rect
  const rect = (r: readonly number[]): [number, number, number, number] => [r[0] * W, (1 - r[3]) * H, (r[2] - r[0]) * W, (r[3] - r[1]) * H];
  let seed = 7;
  const R = (): number => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  // solid paints, slightly worn
  for (const [k, col] of [["white", "#e9e6dc"], ["yellow", "#e0b532"]] as const) {
    const [x, y, w, h] = rect(ATLAS[k]); g.fillStyle = col; g.fillRect(x, y, w, h);
  }
  // worn white stripe (crosswalk / lane): paint with scuffs knocked out
  {
    const [x, y, w, h] = rect(ATLAS.wornWhite);
    g.fillStyle = "#e9e6dc"; g.fillRect(x, y, w, h);
    g.globalCompositeOperation = "destination-out";
    for (let i = 0; i < 110; i++) { g.globalAlpha = 0.25 + R() * 0.6; g.beginPath(); g.ellipse(x + R() * w, y + R() * h, 1 + R() * 7, 1 + R() * 3, R() * 3, 0, 6.3); g.fill(); }
    g.globalAlpha = 1; g.globalCompositeOperation = "source-over";
  }
  // manhole: cast-iron disc with a raised diamond tread and rim
  {
    const [x, y, w] = rect(ATLAS.manhole);
    const cx = x + w / 2, cy = y + w / 2, r = w / 2 - 4;
    g.save(); g.beginPath(); g.arc(cx, cy, r, 0, 6.283); g.clip();
    g.fillStyle = "#3a3936"; g.fillRect(x, y, w, w);
    g.strokeStyle = "#57554f"; g.lineWidth = 3;
    for (let k = -12; k <= 12; k++) {
      g.beginPath(); g.moveTo(cx + k * 12 - 160, cy - 160); g.lineTo(cx + k * 12 + 160, cy + 160); g.stroke();
      g.beginPath(); g.moveTo(cx + k * 12 + 160, cy - 160); g.lineTo(cx + k * 12 - 160, cy + 160); g.stroke();
    }
    g.fillStyle = "rgba(120,80,50,0.35)";
    for (let i = 0; i < 40; i++) { g.beginPath(); g.arc(x + R() * w, y + R() * w, 3 + R() * 14, 0, 6.3); g.fill(); }
    g.fillStyle = "#4a4844"; g.beginPath(); g.arc(cx, cy, r * 0.32, 0, 6.283); g.fill();
    g.fillStyle = "#26251f"; g.font = "bold 22px sans-serif"; g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText("EAUX", cx, cy);
    g.restore();
    g.strokeStyle = "#2a2926"; g.lineWidth = 8; g.beginPath(); g.arc(cx, cy, r - 3, 0, 6.283); g.stroke();
  }
  // gutter grate: steel bars over black
  for (const k of ["grate", "drain"] as const) {
    const [x, y, w, h] = rect(ATLAS[k]);
    g.fillStyle = "#4b4a46"; g.fillRect(x + 4, y + 4, w - 8, h - 8);
    g.fillStyle = "#0d0d0c";
    const n = k === "grate" ? 9 : 6;
    for (let i = 0; i < n; i++) g.fillRect(x + 14 + (i * (w - 28)) / n, y + 14, (w - 28) / n - 7, h - 28);
  }
  // enamel street plaques
  const plaque = (r: readonly number[], text: string, sub: string, bg: string): void => {
    const [x, y, w, h] = rect(r);
    g.fillStyle = "#e8e4da"; g.fillRect(x, y, w, h);
    g.fillStyle = bg; g.fillRect(x + 6, y + 6, w - 12, h - 12);
    g.strokeStyle = "#e8e4da"; g.lineWidth = 3; g.strokeRect(x + 12, y + 12, w - 24, h - 24);
    g.fillStyle = "#f2efe6"; g.textAlign = "center"; g.textBaseline = "middle";
    g.font = `bold ${Math.round(h * 0.3)}px serif`; g.fillText(text, x + w / 2, y + h * 0.44);
    g.font = `${Math.round(h * 0.13)}px serif`; g.fillText(sub, x + w / 2, y + h * 0.74);
  };
  plaque(ATLAS.plaqueEaux, "SERVICE DES EAUX", "RÉSERVOIR N° 4  ·  1898", "#1f4a72");
  plaque(ATLAS.plaqueRue, "RUE DU BLOC", "4e ARRt", "#1f4a72");
  // pressure gauge face
  {
    const [x, y, w] = rect(ATLAS.gauge);
    const cx = x + w / 2, cy = y + w / 2, r = w / 2 - 6;
    g.fillStyle = "#b08d57"; g.beginPath(); g.arc(cx, cy, r, 0, 6.283); g.fill();
    g.fillStyle = "#f1ece0"; g.beginPath(); g.arc(cx, cy, r * 0.82, 0, 6.283); g.fill();
    g.strokeStyle = "#222"; g.lineWidth = 2;
    for (let k = 0; k <= 10; k++) {
      const a = Math.PI * 0.75 + (k / 10) * Math.PI * 1.5;
      g.beginPath(); g.moveTo(cx + Math.cos(a) * r * 0.62, cy + Math.sin(a) * r * 0.62); g.lineTo(cx + Math.cos(a) * r * 0.76, cy + Math.sin(a) * r * 0.76); g.stroke();
    }
    g.strokeStyle = "#a31b12"; g.lineWidth = 3; g.beginPath(); g.moveTo(cx, cy); g.lineTo(cx + r * 0.55, cy - r * 0.3); g.stroke();
  }
  // hazard hatching (yellow/black)
  {
    const [x, y, w, h] = rect(ATLAS.hatch);
    g.save(); g.beginPath(); g.rect(x, y, w, h); g.clip();
    g.fillStyle = "#1b1b1b"; g.fillRect(x, y, w, h);
    g.fillStyle = "#d9a924";
    for (let k = -8; k < 16; k++) { g.beginPath(); g.moveTo(x + k * 24, y); g.lineTo(x + k * 24 + 12, y); g.lineTo(x + k * 24 + 12 - h, y + h); g.lineTo(x + k * 24 - h, y + h); g.fill(); }
    g.restore();
  }
  // oil stain + crack decals (alpha)
  {
    const [x, y, w, h] = rect(ATLAS.oil);
    for (let i = 0; i < 18; i++) {
      const gr = g.createRadialGradient(x + w / 2 + (R() - 0.5) * w * 0.4, y + h / 2 + (R() - 0.5) * h * 0.4, 0, x + w / 2, y + h / 2, w * 0.45);
      gr.addColorStop(0, "rgba(15,14,12,0.35)"); gr.addColorStop(1, "rgba(15,14,12,0)");
      g.fillStyle = gr; g.fillRect(x, y, w, h);
    }
    const [cx0, cy0, cw, ch] = rect(ATLAS.crack);
    g.strokeStyle = "rgba(38,36,32,0.62)"; g.lineCap = "round";
    for (let k = 0; k < 4; k++) {
      let px = cx0 + cw * 0.1, py = cy0 + ch * (0.2 + R() * 0.6);
      g.lineWidth = 1.2 + R() * 1.3; g.beginPath(); g.moveTo(px, py);
      while (px < cx0 + cw * 0.9) { px += 6 + R() * 12; py += (R() - 0.5) * 14; py = Math.max(cy0 + 4, Math.min(cy0 + ch - 4, py)); g.lineTo(px, py); }
      g.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  t.userData.shared = true;
  atlasTex = t;
  return t;
}

// ---------------------------------------------------------------------------
// geometry

function quadGeo(p: Piece): THREE.BufferGeometry {
  const f = p.facing!;
  let g: THREE.PlaneGeometry;
  if (f === "py") { g = new THREE.PlaneGeometry(p.x1 - p.x0, p.z1 - p.z0); g.rotateX(-Math.PI / 2); }
  else if (f === "px" || f === "nx") { g = new THREE.PlaneGeometry(p.z1 - p.z0, p.y1 - p.y0); g.rotateY(f === "px" ? Math.PI / 2 : -Math.PI / 2); }
  else { g = new THREE.PlaneGeometry(p.x1 - p.x0, p.y1 - p.y0); if (f === "nz") g.rotateY(Math.PI); }
  const x = f === "px" ? p.x1 : f === "nx" ? p.x0 : (p.x0 + p.x1) / 2;
  const y = f === "py" ? p.y1 : (p.y0 + p.y1) / 2;
  const z = f === "pz" ? p.z1 : f === "nz" ? p.z0 : (p.z0 + p.z1) / 2;
  g.translate(x, y, z);
  return g;
}

function pieceGeo(p: Piece, radial: number): THREE.BufferGeometry {
  const sx = Math.max(p.x1 - p.x0, 0.004), sy = Math.max(p.y1 - p.y0, 0.004), sz = Math.max(p.z1 - p.z0, 0.004);
  const cx = (p.x0 + p.x1) / 2, cy = (p.y0 + p.y1) / 2, cz = (p.z0 + p.z1) / 2;
  let g: THREE.BufferGeometry;
  if (p.shape === "quad") g = quadGeo(p);
  else if (p.shape === "cylY") { g = new THREE.CylinderGeometry(Math.min(sx, sz) / 2, Math.min(sx, sz) / 2, sy, radial); }
  else if (p.shape === "cylX") { g = new THREE.CylinderGeometry(Math.min(sy, sz) / 2, Math.min(sy, sz) / 2, sx, radial); g.rotateZ(Math.PI / 2); }
  else if (p.shape === "cylZ") { g = new THREE.CylinderGeometry(Math.min(sx, sy) / 2, Math.min(sx, sy) / 2, sz, radial); g.rotateX(Math.PI / 2); }
  else g = new THREE.BoxGeometry(sx, sy, sz);
  if (p.shape !== "quad") {
    // cylinders hug the wall: shift so they sit inside the AABB centre
    g.translate(cx, cy, cz);
  }
  if (p.uv) {
    const uv = g.getAttribute("uv");
    const [u0, v0, u1, v1] = p.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + uv.getX(i) * (u1 - u0), v0 + uv.getY(i) * (v1 - v0));
  } else if (p.tile) worldProjectUV(g, p.tile);
  const n = g.getAttribute("position").count;
  const col = new Float32Array(n * 3);
  const c = new THREE.Color(p.color);
  for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const ng = g.index ? g.toNonIndexed() : g;
  if (ng !== g) g.dispose();
  return ng;
}

export function materialFor(m: Mat, theme: "day" | "neon"): THREE.Material {
  switch (m) {
    case "asphalt": return new THREE.MeshStandardMaterial(surfaceMaps("concrete", { color: 0xffffff, vertexColors: true, roughness: 0.95, metalness: 0 }, 0.6));
    case "pave": return new THREE.MeshStandardMaterial(surfaceMaps("paving", { color: 0xffffff, vertexColors: true, roughness: 0.95, metalness: 0 }, 0.85));
    case "stone": return new THREE.MeshStandardMaterial(surfaceMaps("trim", { color: 0xffffff, vertexColors: true, roughness: 0.92, metalness: 0 }, 0.8));
    case "steel": return new THREE.MeshStandardMaterial(surfaceMaps("surface", { color: 0xffffff, vertexColors: true, roughness: 0.62, metalness: 0.45 }, 0.9));
    case "metal": return new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.5, metalness: 0.55 });
    case "copper": return new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.38, metalness: 0.85, envMapIntensity: 1.3 });
    case "water": return new THREE.MeshStandardMaterial({ color: 0x24414a, roughness: 0.06, metalness: 0.2, envMapIntensity: 1.8, transparent: true, opacity: 0.88, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    case "lamp": return new THREE.MeshBasicMaterial({ color: theme === "day" ? 0xfff0d0 : 0xffe2a8, toneMapped: false });
    case "shop": {
      const t = mapTextures();
      return new THREE.MeshStandardMaterial(surfaceMaps("shopwin", {
        color: "#ffffff", roughness: 0.34, metalness: 0.18, emissive: "#ffffff", emissiveMap: t.shopwinGlow, emissiveIntensity: theme === "day" ? 0.55 : 1.2,
      }, 1.0));
    }
    case "paint": return new THREE.MeshStandardMaterial({
      map: paintAtlas(), color: 0xffffff, vertexColors: true, roughness: 0.82, metalness: 0, alphaTest: 0.4, transparent: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
  }
}

/** Pieces → one merged mesh per material. */
export function buildPieces(pieces: Piece[], name: string, theme: "day" | "neon", shadows: boolean, lowSpec: boolean): THREE.Group {
  const groups = new Map<Mat, THREE.BufferGeometry[]>();
  const radial = lowSpec ? 6 : 12;
  for (const p of pieces) (groups.get(p.mat) ?? groups.set(p.mat, []).get(p.mat)!).push(pieceGeo(p, radial));
  const root = new THREE.Group();
  root.name = name;
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[${name}] merge failed for ${mat}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, materialFor(mat, theme));
    mesh.name = `${name}:${mat}`;
    mesh.castShadow = shadows && (mat === "metal" || mat === "steel" || mat === "copper" || mat === "stone");
    mesh.receiveShadow = mat !== "lamp";
    if (mat === "paint") mesh.renderOrder = 1;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  return root;
}

// ---------------------------------------------------------------------------
// shared emitters

/** a face run that may be interrupted by another collider: try whole, then halves */
export function placeOrSplit(S: Scene, f: Face, mat: Mat, tag: string, t: number, y: number, w: number, h: number, proud: number, color: number, tile = 1.2): void {
  if (S.onFace(f, mat, tag, t, y, w, h, proud, color, { tile })) return;
  if (w < 0.6) return;
  placeOrSplit(S, f, mat, tag, t - w / 4, y, w / 2 - 0.01, h, proud, color, tile);
  placeOrSplit(S, f, mat, tag, t + w / 4, y, w / 2 - 0.01, h, proud, color, tile);
}

export interface FacadeStyle {
  plinth: number; plinth2: number; cornice: number; cornice2: number; string: number; pipe: number; lamp: number;
  /** plaque atlas cell, or null */
  plaque: readonly [number, number, number, number] | null;
  lanterns: boolean;
}
/** Plinth course, string course + cornice, downpipes with shoes and brackets,
 *  wall lanterns, a street plaque — the street-facing skin of a city block. */
export function dressFacade(S: Scene, f: Face, st: FacadeStyle, seed: number, lowSpec: boolean): void {
  const W = f.t1 - f.t0, top = f.y1;
  if (W < 1.5) return;
  const tm = (f.t0 + f.t1) / 2;
  const plinthH = Math.min(0.75, top * 0.25);
  const segs = Math.max(1, Math.round(W / 2.4));
  for (let k = 0; k < segs; k++) {
    const t0 = f.t0 + (W * k) / segs, t1 = f.t0 + (W * (k + 1)) / segs;
    placeOrSplit(S, f, "stone", "plinth", (t0 + t1) / 2, plinthH / 2, t1 - t0 - 0.02, plinthH, 0.08, k % 2 ? st.plinth : st.plinth2);
  }
  if (top > 4) {
    placeOrSplit(S, f, "stone", "cornice", tm, top - 0.22, W, 0.44, 0.22, st.cornice, 1.5);
    placeOrSplit(S, f, "stone", "cornice2", tm, top - 0.58, W, 0.12, 0.12, st.cornice2, 1.5);
    placeOrSplit(S, f, "stone", "string", tm, 3.6, W, 0.18, 0.1, st.string, 1.5);
  }
  const pipeT: number[] = [f.t0 + 0.35, f.t1 - 0.35];
  if (W > 16) for (let t = f.t0 + 14; t < f.t1 - 6; t += 14) pipeT.push(t);
  for (const t of pipeT) {
    const y1 = top - (top > 4 ? 0.6 : 0.05);
    if (y1 < 1.2) continue;
    if (!S.onFace(f, "metal", "downpipe", t, (y1 + 0.05) / 2, 0.14, y1 - 0.05, 0.16, st.pipe, { shape: "cyl" })) continue;
    S.onFace(f, "metal", "shoe", t, 0.17, 0.22, 0.3, 0.2, st.pipe);
    for (let y = 1.5; y < y1 - 0.3; y += 2.2) S.onFace(f, "metal", "bracket", t, y, 0.22, 0.06, 0.2, st.pipe);
  }
  if (st.lanterns && top > 4 && !lowSpec) {
    const n = Math.max(1, Math.floor(W / 9));
    for (let k = 0; k < n; k++) {
      const t = f.t0 + (W * (k + 0.5)) / n;
      if (S.onFace(f, "metal", "lanternArm", t, 3.05, 0.08, 0.5, 0.24, st.lamp)) {
        S.onFace(f, "metal", "lanternCap", t, 3.38, 0.26, 0.08, 0.26, st.lamp);
        S.onFace(f, "lamp", "lanternGlass", t, 3.2, 0.18, 0.24, 0.22, 0xffffff);
      }
    }
  }
  if (st.plaque && h01(seed, f.sign + (f.axis === "x" ? 2 : 5), 31) < 0.6) {
    S.onFace(f, "paint", "plaque", f.t0 + Math.min(1.6, W * 0.3), 2.65, 0.9, 0.45, 0.03, 0xffffff,
      { shape: "quad", uv: [...st.plaque] as [number, number, number, number] });
  }
}

/** Granite kerb stones along the outer edge of the ring `o` around `a`,
 *  skipped where they would fall inside another block's pavement. */
export function kerbRing(S: Scene, a: { x0: number; x1: number; z0: number; z1: number }, o: number, kerb: number,
  inPavement: (x: number, z: number) => boolean, colors: [number, number]): void {
  const runs: [number, number, number, number][] = [
    [a.x0 - o, a.x1 + o, a.z0 - o, a.z0 - o + kerb], [a.x0 - o, a.x1 + o, a.z1 + o - kerb, a.z1 + o],
    [a.x0 - o, a.x0 - o + kerb, a.z0 - o + kerb, a.z1 + o - kerb], [a.x1 + o - kerb, a.x1 + o, a.z0 - o + kerb, a.z1 + o - kerb],
  ];
  for (const [x0, x1, z0, z1] of runs) {
    const along = x1 - x0 > z1 - z0;
    const L = along ? x1 - x0 : z1 - z0;
    const n = Math.max(1, Math.round(L / 1.2));
    for (let k = 0; k < n; k++) {
      const t0 = (along ? x0 : z0) + (L * k) / n + 0.008, t1 = (along ? x0 : z0) + (L * (k + 1)) / n - 0.008;
      const sx0 = along ? t0 : x0, sx1 = along ? t1 : x1, sz0 = along ? z0 : t0, sz1 = along ? z1 : t1;
      const P = S.play;
      if (sx0 < P.x0 || sx1 > P.x1 || sz0 < P.z0 || sz1 > P.z1) continue;
      if (inPavement((sx0 + sx1) / 2, (sz0 + sz1) / 2)) continue;
      S.ground("stone", "kerb", sx0, sx1, sz0, sz1, 0.05, h01(k, Math.round(t0 * 3), 9) < 0.5 ? colors[0] : colors[1], { tile: 1.2 });
    }
  }
}

/** Manholes, grates, oil stains and cracks on open ground at least
 *  `clearance` from every collider. */
export function streetClutter(S: Scene, n: number, clearance: number, seed: number, kinds: ("manhole" | "oil" | "crack" | "grate")[]): void {
  const P = S.play;
  let placed = 0;
  for (let k = 0; k < n * 4 && placed < n; k++) {
    const x = P.x0 + 4 + h01(k, 1, seed) * (P.x1 - P.x0 - 8), z = P.z0 + 4 + h01(k, 2, seed) * (P.z1 - P.z0 - 8);
    const kind = kinds[k % kinds.length];
    const s = kind === "manhole" ? 0.8 : kind === "grate" ? 0.9 : 1.6 + h01(k, 3, seed) * 1.4;
    const zone = { x0: x - s / 2 - clearance, x1: x + s / 2 + clearance, z0: z - s / 2 - clearance, z1: z + s / 2 + clearance, y0: -1, y1: 1 };
    if (!S.clear(zone)) continue;
    const reg = kind === "manhole" ? ATLAS.manhole : kind === "grate" ? ATLAS.grate : kind === "oil" ? ATLAS.oil : ATLAS.crack;
    const sz = kind === "grate" ? s * 0.5 : s;
    if (S.ground("paint", kind, x - s / 2, x + s / 2, z - sz / 2, z + sz / 2, 0.02 + (placed % 7) * 0.0015, 0xffffff,
      { quad: true, uv: [...reg] as [number, number, number, number] })) placed++;
  }
}
